import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { Skeleton } from '@/components/ui/skeleton'
import { useTRPC } from '@/trpc/react'
import { COLORS } from '@repo/shared_utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, XAxis, YAxis } from 'recharts'

export const Route = createFileRoute('/dashboard/admin/employees/statistics')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  const { data: employeeStatistics, isPending: isLoadingEmployeeStatistics } = useQuery({
    ...api.employee.getEmployeeStatistics.queryOptions(),
  })

  if (isLoadingEmployeeStatistics) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Employee Statistics" text="View your employee statistics." />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </DashboardShell>
    )
  }

  if (!employeeStatistics) {
    return (
      <DashboardShell>
        <DashboardHeader heading="Employee Statistics" text="View your employee statistics." />
        <Card>
          <CardContent className="flex h-64 items-center justify-center">
            <p className="text-muted-foreground">No employee data available</p>
          </CardContent>
        </Card>
      </DashboardShell>
    )
  }

  // Transform data for charts
  const clientsData = Object.entries(employeeStatistics.employeesByClient).map(
    ([name, value]) => ({
      name,
      value,
    }),
  )

  const salaryData = Object.entries(employeeStatistics.monthlyRateDistribution).map(
    ([range, count]) => ({
      range,
      count,
    }),
  )

  const hireYearsData = Object.entries(employeeStatistics.hireYearsDistribution)
    .map(([year, count]) => ({ year, count }))
    .sort((a, b) => parseInt(a.year) - parseInt(b.year))

  const departmentData = Object.entries(employeeStatistics.departmentDistribution).map(
    ([name, value]) => ({
      name,
      value,
    }),
  )

  const positionData = Object.entries(employeeStatistics.positionDistribution)
    .map(([name, value]) => ({
      name,
      value,
    }))
    .sort((a, b) => b.value - a.value)

  const genderData = Object.entries(employeeStatistics.genderDistribution).map(
    ([name, value]) => ({
      name,
      value,
    }),
  )

  const payBasisData = Object.entries(employeeStatistics.payBasisDistribution).map(
    ([name, value]) => ({
      name,
      value,
    }),
  )

  const ageData = Object.entries(employeeStatistics.ageDistribution).map(([group, count]) => ({
    group,
    count,
  }))

  const tenureData = Object.entries(employeeStatistics.tenureDistribution).map(
    ([group, count]) => ({
      group,
      count,
    }),
  )

  // const paymentMethodData = Object.entries(employeeStatistics.paymentMethodDistribution).map(
  //   ([name, value]) => ({
  //     name,
  //     value,
  //   }),
  // )

  const chartConfig = {
    count: {
      label: 'Count',
      color: 'hsl(var(--chart-1))',
    },
    value: {
      label: 'Employees',
      color: 'hsl(var(--chart-1))',
    },
  } satisfies ChartConfig

  return (
    <DashboardShell>
      <DashboardHeader
        heading="Employee Statistics"
        text={`Comprehensive analytics for ${employeeStatistics.totalEmployees} employees.`}
      />

      {/* Overview Stats */}
      <div className="grid gap-4 mb-6 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Employees</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: COLORS[0] }}>
              {employeeStatistics.totalEmployees}
            </div>
            <div className="flex items-center mt-2">
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: COLORS[0],
                    width: '100%',
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Verified</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: COLORS[1] }}>
              {employeeStatistics.verifiedCount}
            </div>
            <div className="flex items-center mt-2">
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: COLORS[1],
                    width: `${(employeeStatistics.verifiedCount / employeeStatistics.totalEmployees) * 100}%`,
                  }}
                />
              </div>
              <span className="text-sm text-muted-foreground ml-2">
                {Math.round(
                  (employeeStatistics.verifiedCount / employeeStatistics.totalEmployees) * 100,
                )}
                %
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Not Verified / Inactive</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: COLORS[7] }}>
              {employeeStatistics.unverifiedCount}
            </div>
            <div className="flex items-center mt-2">
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: COLORS[7],
                    width: `${(employeeStatistics.unverifiedCount / employeeStatistics.totalEmployees) * 100}%`,
                  }}
                />
              </div>
              <span className="text-sm text-muted-foreground ml-2">
                {Math.round(
                  (employeeStatistics.unverifiedCount / employeeStatistics.totalEmployees) * 100,
                )}
                %
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Employees by Client - Donut Chart */}
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle>Employees by Client</CardTitle>
            <CardDescription>Distribution across different clients</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[300px]">
              <PieChart>
                <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                <Pie
                  data={clientsData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  strokeWidth={5}
                >
                  {clientsData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Monthly Rate Distribution - Bar Chart */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Salary Distribution</CardTitle>
            <CardDescription>Monthly rate ranges across employees</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <BarChart data={salaryData}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="range" tickLine={false} tickMargin={10} axisLine={false} />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="count" fill={COLORS[0]} radius={4} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Hire Years - Bar Chart */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Hiring Timeline</CardTitle>
            <CardDescription>Employee hires by year</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <BarChart data={hireYearsData}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="year" tickLine={false} tickMargin={10} axisLine={false} />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="count" fill={COLORS[1]} radius={4} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Department Distribution - Donut Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Departments</CardTitle>
            <CardDescription>Employee distribution by department</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[300px]">
              <PieChart>
                <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                <Pie
                  data={departmentData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  strokeWidth={5}
                >
                  {departmentData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Gender Distribution - Donut Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Gender Distribution</CardTitle>
            <CardDescription>Employee gender breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[300px]">
              <PieChart>
                <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                <Pie
                  data={genderData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  strokeWidth={5}
                >
                  {genderData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Pay Basis Distribution - Donut Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Pay Basis</CardTitle>
            <CardDescription>Hourly vs Daily vs Monthly</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[300px]">
              <PieChart>
                <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                <Pie
                  data={payBasisData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  strokeWidth={5}
                >
                  {payBasisData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Age Distribution - Bar Chart */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Age Distribution</CardTitle>
            <CardDescription>Employee age groups</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <BarChart data={ageData}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="group" tickLine={false} tickMargin={10} axisLine={false} />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="count" fill={COLORS[2]} radius={4} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Tenure Distribution - Bar Chart */}
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle>Tenure Distribution</CardTitle>
            <CardDescription>Years of service</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px] w-[400px]">
              <BarChart data={tenureData}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="group"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  fontSize={12}
                  // interval={0}
                />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="count" fill={COLORS[3]} radius={4} maxBarSize={60} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Position Distribution - Limited to top positions */}
        <Card className="md:col-span-3">
          <CardHeader>
            <CardTitle>Top Positions</CardTitle>
            <CardDescription>Most common employee positions</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <BarChart data={positionData.slice(0, 10)}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  fontSize={12}
                />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="value" fill={COLORS[4]} radius={4} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  )
}
